import React, { useState } from 'react';
import { useGameStore } from '@/store/gameStore';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';

export const SettingsScreen: React.FC = () => {
  const { setScreen } = useGameStore();
  const [settings, setSettings] = useState({
    masterVolume: 80,
    musicVolume: 70,
    sfxVolume: 80,
    brightness: 100,
    subtitles: true,
    fullscreen: false,
  });

  const handleVolumeChange = (key: string, value: number[]) => {
    setSettings({ ...settings, [key]: value[0] });
  };

  const handleToggle = (key: string) => {
    setSettings({ ...settings, [key]: !settings[key as keyof typeof settings] });
  };

  return (
    <div className="w-full h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-black overflow-auto">
      <div className="max-w-2xl mx-auto p-6">
        <h1 className="text-4xl font-bold text-center text-orange-500 mb-8">Settings</h1>

        <div className="space-y-6">
          {/* Audio Settings */}
          <Card className="p-6 bg-slate-800 border-orange-500">
            <h2 className="text-2xl font-bold text-orange-500 mb-6">Audio</h2>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-white font-semibold">Master Volume</label>
                  <span className="text-orange-400">{settings.masterVolume}%</span>
                </div>
                <Slider
                  value={[settings.masterVolume]}
                  onValueChange={(value) => handleVolumeChange('masterVolume', value)}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-white font-semibold">Music Volume</label>
                  <span className="text-orange-400">{settings.musicVolume}%</span>
                </div>
                <Slider
                  value={[settings.musicVolume]}
                  onValueChange={(value) => handleVolumeChange('musicVolume', value)}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-white font-semibold">SFX Volume</label>
                  <span className="text-orange-400">{settings.sfxVolume}%</span>
                </div>
                <Slider
                  value={[settings.sfxVolume]}
                  onValueChange={(value) => handleVolumeChange('sfxVolume', value)}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>
            </div>
          </Card>

          {/* Graphics Settings */}
          <Card className="p-6 bg-slate-800 border-orange-500">
            <h2 className="text-2xl font-bold text-orange-500 mb-6">Graphics</h2>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-white font-semibold">Brightness</label>
                  <span className="text-orange-400">{settings.brightness}%</span>
                </div>
                <Slider
                  value={[settings.brightness]}
                  onValueChange={(value) => handleVolumeChange('brightness', value)}
                  min={50}
                  max={150}
                  step={1}
                  className="w-full"
                />
              </div>
            </div>
          </Card>

          {/* Gameplay Settings */}
          <Card className="p-6 bg-slate-800 border-orange-500">
            <h2 className="text-2xl font-bold text-orange-500 mb-6">Gameplay</h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-white font-semibold">Subtitles</label>
                <button
                  onClick={() => handleToggle('subtitles')}
                  className={`px-4 py-2 rounded font-semibold transition ${
                    settings.subtitles
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-600 text-gray-300'
                  }`}
                >
                  {settings.subtitles ? 'On' : 'Off'}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-white font-semibold">Fullscreen</label>
                <button
                  onClick={() => handleToggle('fullscreen')}
                  className={`px-4 py-2 rounded font-semibold transition ${
                    settings.fullscreen
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-600 text-gray-300'
                  }`}
                >
                  {settings.fullscreen ? 'On' : 'Off'}
                </button>
              </div>
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-4 pt-6">
            <Button
              onClick={() => setScreen('title')}
              className="py-3 text-lg font-bold bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
            >
              Back
            </Button>
            <Button
              onClick={() => setScreen('title')}
              className="py-3 text-lg font-bold bg-orange-600 hover:bg-orange-700 text-white rounded-lg"
            >
              Save & Return
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
